"use client";

import React, { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import "./globals.css";
import "@xterm/xterm/css/xterm.css";
import { Sidebar } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { CommandPalette } from "@/components/layout/CommandPalette";
import { ToastProvider } from "@/components/ui/Toast";
import { DialogProvider } from "@/components/ui/Dialog";
import { AuthProvider } from "@/lib/auth/context";
import { ClusterProvider } from "@/lib/cluster/context";
import { ThemeProvider } from "@/lib/theme/context";
import { LanguageProvider } from "@/lib/i18n/context";
import { THEME_CSS, LIGHT_THEME_IDS, DEFAULT_THEME } from "@/lib/theme/themes";

function AppLayoutContent({ children }: { children: React.ReactNode }) {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const pathname = usePathname();
  const isAuthPage =
    pathname === "/login" ||
    pathname === "/setup" ||
    pathname === "/recovery" ||
    pathname.startsWith("/recovery/");

  useEffect(() => {
    setMobileNavigationOpen(false);
  }, [pathname]);

  if (isAuthPage) {
    return <main className="min-h-screen w-full bg-surface-root text-themed-textPrimary">{children}</main>;
  }

  return (
    <div className="flex min-h-screen w-full antialiased bg-surface-root text-themed-textPrimary transition-colors">
      <Sidebar className="hidden lg:flex" />
      {mobileNavigationOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-40 bg-surface-overlay backdrop-blur-sm lg:hidden"
          onClick={() => setMobileNavigationOpen(false)}
        />
      )}
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-transform duration-200 ease-out lg:hidden ${
          mobileNavigationOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={!mobileNavigationOpen}
      >
        <Sidebar className="w-72 max-w-[85vw] shadow-2xl" onNavigate={() => setMobileNavigationOpen(false)} />
      </div>
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          onOpenCommandPalette={() => setCommandPaletteOpen(true)}
          onOpenNavigation={() => setMobileNavigationOpen(true)}
        />
        <main key={pathname} className="flex-1 p-4 sm:p-6 overflow-y-auto max-w-[1800px] w-full mx-auto">
          {children}
        </main>
      </div>
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onOpen={() => setCommandPaletteOpen(true)}
      />
    </div>
  );
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <title>K8s Websites Manager - Hosting Control Plane</title>
        <meta
          name="description"
          content="Modern local Kubernetes hosting control plane for web applications and multi-tenant sites."
        />
        <style
          id="kubehost-theme-variables"
          dangerouslySetInnerHTML={{ __html: THEME_CSS }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                try {
                  var stored = localStorage.getItem('kubehost_theme') || ${JSON.stringify(DEFAULT_THEME)};
                  var lightThemes = ${JSON.stringify(LIGHT_THEME_IDS)};
                  var isLight = lightThemes.indexOf(stored) !== -1;
                  document.documentElement.setAttribute('data-theme', stored);
                  document.documentElement.setAttribute('data-theme-mode', isLight ? 'light' : 'dark');
                  if (isLight) {
                    document.documentElement.classList.remove('dark');
                    document.documentElement.classList.add('light');
                  } else {
                    document.documentElement.classList.remove('light');
                    document.documentElement.classList.add('dark');
                  }
                } catch(e) {}
              })();
            `,
          }}
        />
      </head>
      <body className="bg-surface-root text-themed-textPrimary min-h-screen antialiased selection:bg-[var(--accent-primary)]/25 selection:text-[var(--accent-primary)] transition-colors">
        <ThemeProvider>
          <LanguageProvider>
            <ToastProvider>
              <DialogProvider>
                <AuthProvider>
                  <ClusterProvider>
                    <AppLayoutContent>{children}</AppLayoutContent>
                  </ClusterProvider>
                </AuthProvider>
              </DialogProvider>
            </ToastProvider>
          </LanguageProvider>
        </ThemeProvider>
      </body>
    </html>
  );
}
